<template>
  <div class="space-y-3">
    <div>
      <label :for="`add-vehicle-ymm-${_uid}`" class="block text-sm font-medium text-gray-700">Year, Make, Model <span class="text-red-500">*</span></label>
      <input 
        type="text" 
        :id="`add-vehicle-ymm-${_uid}`" 
        :value="modelValue.yearMakeModel" 
        @input="updateDetail('yearMakeModel', $event.target.value)"
        placeholder="e.g., 2023 Toyota Camry"
        class="input-responsive"
        required
        aria-describedby="add-vehicle-ymm-error"
      />
      <p v-if="errors.yearMakeModel" id="add-vehicle-ymm-error" class="error-message">{{ errors.yearMakeModel }}</p>
    </div>
    <div>
      <label :for="`add-vehicle-vin-${_uid}`" class="block text-sm font-medium text-gray-700">VIN</label>
      <input 
        type="text" 
        :id="`add-vehicle-vin-${_uid}`" 
        :value="modelValue.vin"
        @input="updateDetail('vin', $event.target.value)"
        placeholder="Vehicle Identification Number (Optional)"
        class="input-responsive"
        aria-describedby="add-vehicle-vin-error"
      />
       <p v-if="errors.vin" id="add-vehicle-vin-error" class="error-message">{{ errors.vin }}</p>
    </div>
    <div>
      <label :for="`add-vehicle-usage-${_uid}`" class="block text-sm font-medium text-gray-700">Usage</label>
      <input 
        type="text" 
        :id="`add-vehicle-usage-${_uid}`" 
        :value="modelValue.usage"
        @input="updateDetail('usage', $event.target.value)"
        placeholder="Pleasure, Commute, Business, etc."
        class="input-responsive"
        aria-describedby="add-vehicle-usage-error"
      />
      <p v-if="errors.usage" id="add-vehicle-usage-error" class="error-message">{{ errors.usage }}</p>
    </div>
    <div>
      <label :for="`add-vehicle-driver-${_uid}`" class="block text-sm font-medium text-gray-700">Primary Driver</label>
      <input 
        type="text" 
        :id="`add-vehicle-driver-${_uid}`" 
        :value="modelValue.primaryDriver"
        @input="updateDetail('primaryDriver', $event.target.value)"
        placeholder="Name of the main driver"
        class="input-responsive"
        aria-describedby="add-vehicle-driver-error"
      />
      <p v-if="errors.primaryDriver" id="add-vehicle-driver-error" class="error-message">{{ errors.primaryDriver }}</p>
    </div>
    <!-- Add more fields as needed -->
  </div>
</template>

<script setup>
import { defineProps, defineEmits, getCurrentInstance, ref } from 'vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

// Get unique ID for labels/inputs
const instance = getCurrentInstance();
const _uid = instance ? instance.uid : Math.random().toString(36).substring(7);

const props = defineProps({
  modelValue: { 
    type: Object,
    required: true,
    default: () => ({ yearMakeModel: '', vin: '', usage: '', primaryDriver: '' }) 
  }
});

const emit = defineEmits(['update:modelValue']);

// --- Internal Validation Setup ---
const validationSchema = yup.object({
  yearMakeModel: yup.string().required('Year/Make/Model is required'),
  vin: yup.string().length(17, 'VIN must be 17 characters').nullable(),
  usage: yup.string().nullable(),
  primaryDriver: yup.string().nullable(),
});

// Use VeeValidate internally, referencing the props for current values
const { errors, validate: validateInternally } = useForm({
  validationSchema: validationSchema,
  // Important: Pass initial values based on props to allow validation on load if needed
  // and ensure reactivity if props change externally (though unlikely with v-model)
  initialValues: props.modelValue 
});

// Update detail function remains the same - emit changes to parent
const updateDetail = (key, value) => {
  const newValue = { 
    ...props.modelValue, 
    [key]: value 
  };
  emit('update:modelValue', newValue);
  // Optional: Trigger validation manually on input if desired
  // validateInternally(); 
};

// --- Expose validation function to parent ---
defineExpose({
  validate() {
    try {
      // Parse the yearMakeModel input
      const ymmValue = props.modelValue.yearMakeModel?.trim() || '';
      
      // First check: Does the input have content at all?
      if (!ymmValue) {
        errors.yearMakeModel = 'Year/Make/Model is required';
        validateInternally({ force: true }); // Update error display
        return false;
      }
      
      // Check if YMM contains reasonable car info (example: "2023 jeep wrangler")
      // 1. Should contain a year (4 digits)
      // 2. Should have content after the year for make/model
      const yearRegex = /\b(19|20)\d{2}\b/; // Match years from 1900-2099
      const yearMatch = ymmValue.match(yearRegex);
      
      // Extract year and make/model parts
      let valid = true;
      if (!yearMatch) {
        errors.yearMakeModel = 'Please include a valid year (example: 2023 Toyota Camry)';
        valid = false;
      } else {
        // Check if there's content after the year for make/model
        const year = yearMatch[0];
        const afterYear = ymmValue.substring(ymmValue.indexOf(year) + year.length).trim();
        
        if (!afterYear || afterYear.length < 3) {
          errors.yearMakeModel = 'Please include make and model (example: 2023 Toyota Camry)';
          valid = false;
        }
      }
      
      // Check VIN if provided
      const vinValue = props.modelValue.vin?.trim() || '';
      if (vinValue && vinValue.length !== 17) {
        errors.vin = 'VIN must be 17 characters';
        valid = false;
      } else {
        errors.vin = '';
      }
      
      validateInternally({ force: true }); // Update error display
      return valid;
    } catch (error) {
      console.error('Validation error in AddVehicleSection:', error);
      return false;
    }
  }
});

</script>

<style scoped>
.input-responsive {
    @apply mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm;
}
.error-message {
    @apply text-red-500 text-xs italic mt-1;
}
</style> 